import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { DemoBanner } from "@/components/portal/DemoBanner";
import { DocumentUploadCard } from "@/components/portal/DocumentUploadCard";
import { MyDocumentsCard } from "@/components/portal/MyDocumentsCard";
import { claimLoad, listLoads, type LoadRecord } from "@/features/loads/repo";
import {
  formatSettlementAmount,
  listSettlements,
  type SettlementRecord,
} from "@/features/settlements/repo";
import { useAuth } from "@/features/auth/useAuth";

export default function CarrierDashboard() {
  const { user } = useAuth();
  const [loads, setLoads] = useState<LoadRecord[]>([]);
  const [settlements, setSettlements] = useState<SettlementRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [documentsRefreshKey, setDocumentsRefreshKey] = useState(0);

  useEffect(() => {
    if (!user) return;
    Promise.all([listLoads(user), listSettlements(user)])
      .then(([loadData, settlementData]) => {
        setLoads(loadData);
        setSettlements(settlementData);
      })
      .catch(() => {
        setLoads([]);
        setSettlements([]);
      })
      .finally(() => setLoading(false));
  }, [user]);

  const available = useMemo(
    () => loads.filter((load) => !load.carrierProfileId && load.status === "booked"),
    [loads],
  );

  const handleClaim = async (load: LoadRecord) => {
    if (!user) return;
    setClaimingId(load.id);
    try {
      await claimLoad(load.id, user);
      setLoads((current) =>
        current.map((item) =>
          item.id === load.id
            ? {
                ...item,
                carrierProfileId: user.id,
                carrierName: user.name,
                status: "dispatched",
              }
            : item,
        ),
      );
    } finally {
      setClaimingId(null);
    }
  };

  return (
    <>
      <Seo title="Carrier portal" noindex />
      <DemoBanner />
      <h1 className="text-2xl font-bold">Owner-operator portal</h1>
      <p className="mt-1 text-sm text-brand-500">
        Claim loads, manage onboarding documents, and review settlement statements.
      </p>

      <div className="mt-6">
        <DocumentUploadCard
          title="Upload onboarding document"
          description="Send insurance, W-9, or other onboarding paperwork to staff."
          relatedReference="Owner-operator onboarding"
          kinds={["insurance", "w9", "other"]}
          defaultKind="insurance"
          onUploaded={() => setDocumentsRefreshKey((key) => key + 1)}
        />
      </div>

      <div className="mt-6">
        <MyDocumentsCard refreshKey={documentsRefreshKey} />
      </div>

      <h2 className="mt-10 text-lg font-semibold">Settlements</h2>
      <p className="mt-1 text-sm text-brand-500">
        Weekly settlement statements issued by Bull &amp; Bear accounting.
      </p>

      <div className="mt-4 grid gap-3 md:hidden">
        {loading && (
          <p className="rounded-lg border border-brand-100 bg-white p-4 text-sm text-brand-500">
            Loading settlements...
          </p>
        )}
        {!loading && settlements.length === 0 && (
          <p className="rounded-lg border border-dashed border-brand-200 bg-white p-4 text-sm text-brand-500">
            No settlement statements yet.
          </p>
        )}
        {!loading &&
          settlements.map((settlement) => (
            <div key={settlement.id} className="rounded-xl border border-brand-100 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="break-words font-medium text-brand-900">{settlement.reference}</p>
                  <p className="mt-1 text-sm text-brand-500">
                    {settlement.periodStart} - {settlement.periodEnd}
                  </p>
                </div>
                <Badge variant={settlement.status === "paid" ? "success" : "warning"}>
                  {settlement.status}
                </Badge>
              </div>
              <p className="mt-3 text-sm font-semibold text-brand-800">
                {formatSettlementAmount(settlement.amountCents)}
              </p>
            </div>
          ))}
      </div>

      <div className="mt-4 hidden overflow-x-auto rounded-lg border border-brand-100 md:block">
        <table className="w-full text-left text-sm">
          <thead className="bg-brand-50 text-brand-600">
            <tr>
              <th className="px-4 py-2">Reference</th>
              <th className="px-4 py-2">Period</th>
              <th className="px-4 py-2">Amount</th>
              <th className="px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-brand-500">
                  Loading settlements...
                </td>
              </tr>
            )}
            {!loading && settlements.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-brand-500">
                  No settlement statements yet.
                </td>
              </tr>
            )}
            {!loading &&
              settlements.map((settlement) => (
                <tr key={settlement.id} className="border-t border-brand-100">
                  <td className="px-4 py-2 font-medium">{settlement.reference}</td>
                  <td className="px-4 py-2">
                    {settlement.periodStart} – {settlement.periodEnd}
                  </td>
                  <td className="px-4 py-2">{formatSettlementAmount(settlement.amountCents)}</td>
                  <td className="px-4 py-2">
                    <Badge variant={settlement.status === "paid" ? "success" : "warning"}>
                      {settlement.status}
                    </Badge>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <h2 className="mt-10 text-lg font-semibold">Available loads</h2>
      {loading && <p className="mt-4 text-sm text-brand-500">Loading available loads...</p>}

      <div className="mt-4 grid gap-4">
        {!loading && available.length === 0 && (
          <p className="text-sm text-brand-500">No available loads right now.</p>
        )}
        {available.map((load) => (
          <Card key={load.id}>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="break-words">#{load.reference}</CardTitle>
                <Badge>{load.status.replace(/_/g, " ")}</Badge>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="break-words text-sm text-brand-600">
                  {load.origin} &rarr; {load.destination}
                </p>
                <p className="mt-1 text-sm text-brand-500">Pickup {load.pickup}</p>
              </div>
              <Button
                variant="accent"
                className="w-full sm:w-auto"
                disabled={claimingId === load.id}
                onClick={() => void handleClaim(load)}
              >
                {claimingId === load.id ? "Claiming..." : "Claim"}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}
